import React, { useState, useMemo } from "react";
import { X, Plus, Trash2, Save } from "lucide-react";
import { api } from "@/lib/api";
import { toast } from "sonner";

const uid = () => Math.random().toString(36).slice(2, 10);
const today = () => new Date().toISOString().slice(0, 10);

export default function DocumentEditor({ doc, type, clients, settings, symbol = "$", onClose, onSaved, onClientAdded }) {
  const isNew = !doc?.id;
  const [d, setD] = useState(() => ({
    type: doc?.type || type || "invoice",
    client_id: doc?.client_id || "",
    issue_date: doc?.issue_date || today(),
    due_date: doc?.due_date || "",
    tax_rate: doc?.tax_rate ?? (settings?.tax_default || 0),
    discount: doc?.discount ?? 0,
    discount_type: doc?.discount_type || "flat",
    deposit_pct: doc?.deposit_pct ?? (settings?.default_deposit_pct || 0),
    notes: doc?.notes || "",
    status: doc?.status || (type === "quote" ? "draft" : "sent"),
    recurring: doc?.recurring || { enabled: false, interval: "monthly" },
    line_items: (doc?.line_items?.length ? doc.line_items : [{ id: uid(), description: "", qty: 1, rate: 0 }]).map((l) => ({ ...l, id: l.id || uid() })),
  }));
  const [saving, setSaving] = useState(false);
  const [newClient, setNewClient] = useState(false);
  const [clientForm, setClientForm] = useState({ name: "", company: "", email: "", phone: "", address: "" });

  const set = (k, v) => setD((s) => ({ ...s, [k]: v }));
  const addItem = () => set("line_items", [...d.line_items, { id: uid(), description: "", qty: 1, rate: 0 }]);
  const updItem = (id, k, v) => set("line_items", d.line_items.map((l) => (l.id === id ? { ...l, [k]: v } : l)));
  const delItem = (id) => set("line_items", d.line_items.filter((l) => l.id !== id));

  const totals = useMemo(() => {
    const subtotal = d.line_items.reduce((s, l) => s + (Number(l.qty) || 0) * (Number(l.rate) || 0), 0);
    const disc = Number(d.discount) || 0;
    const discAmt = Math.min(d.discount_type === "percent" ? subtotal * disc / 100 : disc, subtotal);
    const base = Math.max(subtotal - discAmt, 0);
    const taxAmt = base * (Number(d.tax_rate) || 0) / 100;
    return { subtotal, discAmt, taxAmt, total: base + taxAmt };
  }, [d]);

  const money = (v) => `${symbol}${(v || 0).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

  const saveClient = async () => {
    if (!clientForm.name.trim()) { toast.error("Client name required"); return; }
    const c = await api.createGenClient(clientForm);
    onClientAdded?.(c);
    set("client_id", c.id);
    setNewClient(false);
    setClientForm({ name: "", company: "", email: "", phone: "", address: "" });
    toast.success("Client added");
  };

  const save = async () => {
    if (d.line_items.every((l) => !l.description.trim())) { toast.error("Add at least one line item"); return; }
    setSaving(true);
    try {
      const payload = {
        ...d,
        tax_rate: Number(d.tax_rate) || 0,
        discount: Number(d.discount) || 0,
        recurring: d.type === "invoice" ? d.recurring : null,
        deposit_pct: d.type === "invoice" ? (Number(d.deposit_pct) || 0) : 0,
        line_items: d.line_items.map((l) => ({ id: l.id, description: l.description, qty: Number(l.qty) || 0, rate: Number(l.rate) || 0 })),
      };
      const saved = isNew ? await api.createGenDocument(payload) : await api.updateGenDocument(doc.id, payload);
      toast.success(isNew ? `${d.type === "quote" ? "Quote" : "Invoice"} created` : "Saved");
      onSaved?.(saved);
      onClose?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const inputCls = "w-full px-3 py-2.5 rounded-lg dark-input text-sm";
  const labelCls = "block text-[10px] font-bold text-zinc-500 uppercase tracking-wider mb-1.5";

  return (
    <div className="fixed inset-0 z-[200] flex items-center justify-center bg-black/80 backdrop-blur-md p-4" data-testid="document-editor-modal">
      <div className="glass-panel bg-[#0a0a0b]/95 rounded-3xl shadow-2xl w-full max-w-3xl relative fade-in overflow-hidden border border-white/10 flex flex-col" style={{ maxHeight: "92vh" }}>
        <div className="px-6 py-4 border-b border-white/10 flex justify-between items-center bg-white/5">
          <h3 className="text-base font-bold text-white">{isNew ? `New ${d.type === "quote" ? "Quote" : "Invoice"}` : `Edit ${d.type === "quote" ? "Quote" : "Invoice"}`}</h3>
          <button onClick={onClose} className="text-zinc-500 hover:text-white" data-testid="doc-editor-close"><X /></button>
        </div>

        <div className="flex-1 overflow-y-auto p-6 space-y-5 hide-scrollbar">
          {/* Client + dates */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <div className="md:col-span-1">
              <label className={labelCls}>Client</label>
              {!newClient ? (
                <div className="flex gap-2">
                  <select value={d.client_id} onChange={(e) => set("client_id", e.target.value)} className={inputCls} data-testid="doc-client-select">
                    <option value="">— Select client —</option>
                    {clients.map((c) => <option key={c.id} value={c.id}>{c.name}{c.company ? ` (${c.company})` : ""}</option>)}
                  </select>
                  <button onClick={() => setNewClient(true)} className="px-3 rounded-lg bg-white/10 text-white text-sm hover:bg-white/20" title="Add client"><Plus size={14} /></button>
                </div>
              ) : (
                <div className="space-y-2 p-3 rounded-lg bg-black/30 border border-white/10">
                  <input value={clientForm.name} onChange={(e) => setClientForm({ ...clientForm, name: e.target.value })} placeholder="Name *" className={inputCls} data-testid="doc-newclient-name" />
                  <input value={clientForm.company} onChange={(e) => setClientForm({ ...clientForm, company: e.target.value })} placeholder="Company" className={inputCls} />
                  <input value={clientForm.email} onChange={(e) => setClientForm({ ...clientForm, email: e.target.value })} placeholder="Email" className={inputCls} />
                  <div className="flex gap-2">
                    <button onClick={saveClient} className="flex-1 px-3 py-2 bg-primary-600 text-white rounded-lg text-xs font-bold hover:bg-primary-500">Save Client</button>
                    <button onClick={() => setNewClient(false)} className="px-3 py-2 bg-white/5 text-zinc-400 rounded-lg text-xs">Cancel</button>
                  </div>
                </div>
              )}
            </div>
            <div><label className={labelCls}>Issue Date</label><input type="date" value={d.issue_date} onChange={(e) => set("issue_date", e.target.value)} className={inputCls} /></div>
            <div><label className={labelCls}>{d.type === "quote" ? "Valid Until" : "Due Date"}</label><input type="date" value={d.due_date} onChange={(e) => set("due_date", e.target.value)} className={inputCls} /></div>
          </div>

          {/* Line items */}
          <div>
            <div className="flex items-center justify-between mb-2">
              <label className={labelCls}>Line Items</label>
              <button onClick={addItem} className="px-3 py-1.5 bg-white/10 text-white rounded-lg text-xs font-bold hover:bg-white/20 flex items-center" data-testid="doc-add-item"><Plus size={12} className="mr-1" /> Add Item</button>
            </div>
            <div className="space-y-2">
              <div className="grid grid-cols-12 gap-2 text-[10px] font-bold text-zinc-500 uppercase px-1">
                <span className="col-span-6">Description</span><span className="col-span-2 text-right">Qty</span><span className="col-span-2 text-right">Rate</span><span className="col-span-2 text-right">Amount</span>
              </div>
              {d.line_items.map((l) => (
                <div key={l.id} className="grid grid-cols-12 gap-2 items-center" data-testid="doc-item-row">
                  <input value={l.description} onChange={(e) => updItem(l.id, "description", e.target.value)} placeholder="Item description" className={`${inputCls} col-span-6`} />
                  <input type="number" value={l.qty} onChange={(e) => updItem(l.id, "qty", e.target.value)} className={`${inputCls} col-span-2 text-right`} />
                  <input type="number" value={l.rate} onChange={(e) => updItem(l.id, "rate", e.target.value)} className={`${inputCls} col-span-2 text-right`} />
                  <div className="col-span-1 text-right text-sm text-white font-medium">{money((Number(l.qty) || 0) * (Number(l.rate) || 0))}</div>
                  <button onClick={() => delItem(l.id)} className="col-span-1 text-zinc-500 hover:text-red-400 flex justify-end"><Trash2 size={14} /></button>
                </div>
              ))}
            </div>
          </div>

          {/* Tax / discount + totals */}
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-3">
              <div>
                <label className={labelCls}>Discount</label>
                <div className="flex gap-2">
                  <input type="number" value={d.discount} onChange={(e) => set("discount", e.target.value)} className={inputCls} />
                  <select value={d.discount_type} onChange={(e) => set("discount_type", e.target.value)} className="px-3 rounded-lg dark-input text-sm">
                    <option value="flat">{symbol}</option>
                    <option value="percent">%</option>
                  </select>
                </div>
              </div>
              <div><label className={labelCls}>Tax Rate (%)</label><input type="number" value={d.tax_rate} onChange={(e) => set("tax_rate", e.target.value)} className={inputCls} /></div>
              {d.type === "invoice" && (
                <div>
                  <label className={labelCls}>Required Deposit (%)</label>
                  <input type="number" min="0" max="100" value={d.deposit_pct} onChange={(e) => set("deposit_pct", e.target.value)} placeholder="0" className={inputCls} data-testid="doc-deposit-pct" />
                  {Number(d.deposit_pct) > 0 && <p className="text-[10px] text-amber-400 mt-1">Client must pay at least {money(totals.total * (Number(d.deposit_pct) || 0) / 100)} upfront.</p>}
                </div>
              )}
              <div><label className={labelCls}>Status</label>
                <select value={d.status} onChange={(e) => set("status", e.target.value)} className={inputCls}>
                  <option value="draft">Draft</option>
                  <option value="sent">Sent</option>
                  {d.type === "invoice" && <option value="paid">Paid</option>}
                </select>
              </div>
            </div>
            <div className="p-4 rounded-xl bg-black/30 border border-white/10 space-y-2 self-start">
              <div className="flex justify-between text-sm text-zinc-400"><span>Subtotal</span><span>{money(totals.subtotal)}</span></div>
              {totals.discAmt > 0 && <div className="flex justify-between text-sm text-teal-400"><span>Discount</span><span>-{money(totals.discAmt)}</span></div>}
              {totals.taxAmt > 0 && <div className="flex justify-between text-sm text-zinc-400"><span>Tax</span><span>{money(totals.taxAmt)}</span></div>}
              <div className="flex justify-between text-lg font-bold text-white pt-2 border-t border-white/10"><span>Total</span><span data-testid="doc-editor-total">{money(totals.total)}</span></div>
            </div>
          </div>

          <div>
            <label className={labelCls}>Notes</label>
            <textarea value={d.notes} onChange={(e) => set("notes", e.target.value)} rows={2} placeholder="Payment terms, thank-you note…" className={inputCls} />
          </div>

          {d.type === "invoice" && (
            <div className="p-4 rounded-xl bg-black/30 border border-white/10" data-testid="doc-recurring">
              <label className="flex items-center gap-2 text-sm text-zinc-300 cursor-pointer select-none">
                <input type="checkbox" checked={!!d.recurring?.enabled} onChange={(e) => set("recurring", { ...d.recurring, enabled: e.target.checked })} className="accent-primary-500" data-testid="doc-recurring-toggle" />
                Make this a recurring invoice (auto-generate &amp; email on a schedule)
              </label>
              {d.recurring?.enabled && (
                <div className="mt-3 flex items-center gap-2">
                  <span className="text-xs text-zinc-500">Repeat</span>
                  <select value={d.recurring.interval} onChange={(e) => set("recurring", { ...d.recurring, interval: e.target.value })} className="px-3 py-2 rounded-lg dark-input text-sm">
                    <option value="weekly">Weekly</option>
                    <option value="monthly">Monthly</option>
                  </select>
                  {d.recurring.next_run && <span className="text-xs text-zinc-500">· next: {String(d.recurring.next_run).slice(0, 10)}</span>}
                </div>
              )}
            </div>
          )}
        </div>

        <div className="px-6 py-4 border-t border-white/10 flex justify-end gap-3 bg-white/5">
          <button onClick={onClose} className="px-5 py-2.5 rounded-xl bg-white/5 text-zinc-300 text-sm font-bold hover:bg-white/10">Cancel</button>
          <button onClick={save} disabled={saving} className="px-6 py-2.5 rounded-xl bg-primary-600 text-white text-sm font-bold hover:bg-primary-500 flex items-center disabled:opacity-60" data-testid="doc-save-btn">
            <Save size={15} className="mr-2" /> {saving ? "Saving…" : isNew ? "Create" : "Save"}
          </button>
        </div>
      </div>
    </div>
  );
}
